import { List, Card, Typography, Button, Divider, Pagination } from 'antd';
import { contractService } from 'contract/services/contractService';
import { ContractInterface } from 'contract/type';
import React, { memo, useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';

const { Title, Text } = Typography;

interface TimelineItem {
  id: number;
  title: string;
  startDate: Date;
  endDate: Date;
  status: 'active' | 'expired' | 'upcoming';
  duration: number;
}

const Contract: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const url = `${window.location.origin}/contract`;
  const [contractList, setContractList] = useState<ContractInterface[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true);
        // Fetch contract list with paging parameters
        const params = new URLSearchParams({
          page: currentPage.toString(),
          page_size: pageSize.toString(),
        });

        const response = await contractService.fetchByContentIds([Number(id)], params);
        setContractList(response.results);
        setTotalCount(response.count);
      } catch (error) {
        console.error('Failed to fetch contract data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, currentPage, pageSize]);

  // Generate Timeline data (only contracts from current page)
  const timelineData = useMemo(() => {
    const items: TimelineItem[] = contractList
      .filter(contract => contract.contract_start_date && contract.contract_expire_date)
      .map(contract => {
        const startDate = new Date(contract.contract_start_date);
        const endDate = new Date(contract.contract_expire_date);
        const duration = Math.ceil(
          (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        const now = new Date();

        return {
          id: contract.id,
          title: contract.title,
          startDate,
          endDate,
          status: now > endDate ? 'expired' : now < startDate ? 'upcoming' : 'active',
          duration,
        } as TimelineItem;
      });

    // Calculate total period
    if (items.length === 0)
      return { items: [], minDate: new Date(), maxDate: new Date(), totalDays: 0 };

    const minDate = new Date(Math.min(...items.map(item => item.startDate.getTime())));
    const maxDate = new Date(Math.max(...items.map(item => item.endDate.getTime())));
    const totalDays = Math.ceil((maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24));

    return { items, minDate, maxDate, totalDays };
  }, [contractList]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#52c41a';
      case 'expired':
        return '#ff4d4f';
      case 'upcoming':
        return '#1890ff';
      default:
        return '#d9d9d9';
    }
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <Title level={4}>Matching Contract ({totalCount})</Title>
      {totalCount > 0 ? (
        <>
          {/* Matching Contract list */}
          <List
            grid={{ gutter: 16, column: 1 }}
            dataSource={contractList}
            renderItem={contractItem => (
              <List.Item>
                <Card
                  title={contractItem.title}
                  extra={
                    <Button type="link" href={`${url}/edit/${contractItem.id}`} target="_blank">
                      Link
                    </Button>
                  }
                >
                  <Text type="secondary">Contract ID: {contractItem.id}</Text>
                  <br />
                  <Text type="secondary">Licensor: {contractItem.licensor_title}</Text>
                  <br />
                  <Text type="secondary">Content Type: {contractItem.content_type}</Text>
                  <br />
                  <Text type="secondary">Service Type: {contractItem.service_type}</Text>
                  <br />
                  {contractItem.contract_start_date && (
                    <Text type="secondary">
                      Contract Start Date:{' '}
                      {new Date(contractItem.contract_start_date).toLocaleDateString()}
                    </Text>
                  )}
                  {contractItem.contract_expire_date && (
                    <>
                      <br />
                      <Text type="secondary">
                        Contract Expire Date:{' '}
                        {new Date(contractItem.contract_expire_date).toLocaleDateString()}
                      </Text>
                    </>
                  )}
                  {contractItem.signed_date && (
                    <>
                      <br />
                      <Text type="secondary">
                        Signed Date: {new Date(contractItem.signed_date).toLocaleDateString()}
                      </Text>
                    </>
                  )}
                  {contractItem.note && (
                    <>
                      <br />
                      <Text>{contractItem.note}</Text>
                    </>
                  )}
                </Card>
              </List.Item>
            )}
          />

          {/* Matching Contract paging */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <Pagination
              current={currentPage}
              total={totalCount}
              pageSize={pageSize}
              showSizeChanger
              showQuickJumper
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} contract${total !== 1 ? 's' : ''}`
              }
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
              pageSizeOptions={['5', '10', '20', '50']}
            />
          </div>

          {/* Contract Timeline (only contracts from current page) */}
          {timelineData.items.length > 0 && (
            <>
              <Divider />
              <Title level={4}>
                Contract Timeline
                <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'normal' }}>
                  {' '}
                  (Showing page {currentPage} contract{contractList.length !== 1 ? 's' : ''})
                </Text>
              </Title>
              <div style={{ marginBottom: '16px' }}>
                <Text type="secondary">
                  <span style={{ color: '#52c41a' }}>■</span> Active Contract &nbsp;
                  <span style={{ color: '#1890ff' }}>■</span> Upcoming Contract &nbsp;
                  <span style={{ color: '#ff4d4f' }}>■</span> Expired Contract
                </Text>
              </div>

              <div style={{ padding: '20px', backgroundColor: '#fafafa', borderRadius: '8px' }}>
                {timelineData.items.map(item => {
                  const msPerDay = 1000 * 60 * 60 * 24;
                  const timeDiff = item.startDate.getTime() - timelineData.minDate.getTime();
                  const dayOffset = timeDiff / msPerDay;
                  const startOffset = (dayOffset / timelineData.totalDays) * 100;
                  const width = (item.duration / timelineData.totalDays) * 100;
                  const tooltipText = `${item.title}\n${item.startDate.toLocaleDateString()}
                   - ${item.endDate.toLocaleDateString()}\n${item.duration} days`;

                  return (
                    <div key={item.id} style={{ marginBottom: '20px' }}>
                      <div style={{ marginBottom: '5px', fontSize: '12px', fontWeight: 'bold' }}>
                        {item.title.length > 40 ? `${item.title.substring(0, 40)}...` : item.title}
                      </div>
                      <div
                        style={{
                          position: 'relative',
                          height: '30px',
                          backgroundColor: '#fff',
                          border: '1px solid #d9d9d9',
                          borderRadius: '4px',
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            left: `${startOffset}%`,
                            width: `${width}%`,
                            height: '100%',
                            backgroundColor: getStatusColor(item.status),
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: 'bold',
                          }}
                          title={tooltipText}
                        >
                          {item.duration}d
                        </div>
                      </div>
                      <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                        {item.startDate.toLocaleDateString()} - {item.endDate.toLocaleDateString()}
                      </div>
                    </div>
                  );
                })}

                {/* Display time axis */}
                <div
                  style={{ marginTop: '20px', borderTop: '1px solid #d9d9d9', paddingTop: '10px' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '10px',
                      color: '#666',
                    }}
                  >
                    <span>{timelineData.minDate.toLocaleDateString()}</span>
                    <span>{timelineData.maxDate.toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <Card>
          <Text type="secondary">No matching contract.</Text>
          <br />
          <Button type="link" href={url} target="_blank">
            Go to Contract Management
          </Button>
        </Card>
      )}
    </div>
  );
};

export default memo(Contract);
